import { forwardRef } from 'react'

import { cn } from '@/lib/utils'

/**
 * A person as initials in a circle: 40px in the rail, 24px in share stacks,
 * 200px on the account page. `filled` (white on `primary-strong`) marks the
 * signed-in user while their own account page is open.
 *
 * The outline and the initials take the two darker blues, not the brand hue:
 * #3c7ff3 carries neither a 3:1 boundary nor readable text on white.
 *
 * `size` is a caller-chosen pixel value rather than a token, so it is the one
 * thing here set inline; with no `size` the component uses the 40px `avatar`
 * token and the `body` ramp step, which is the same 0.4 ratio.
 */
export interface FxAvatarProps extends Omit<React.HTMLAttributes<HTMLSpanElement>, 'children'> {
  initials: string
  size?: number
  /** Solid `primary-strong` instead of an outline — the current user. */
  filled?: boolean
}

export const FxAvatar = forwardRef<HTMLSpanElement, FxAvatarProps>(
  ({ initials, size, filled = false, className, style, ...props }, ref) => (
    <span
      ref={ref}
      className={cn(
        'box-border inline-flex flex-none items-center justify-center rounded-full border-[1.5px] border-primary-strong leading-none',
        filled ? 'bg-primary-strong text-on-primary' : 'bg-surface text-primary-text',
        size === undefined && 'h-avatar w-avatar text-body',
        className,
      )}
      style={
        size === undefined
          ? style
          : { width: size, height: size, fontSize: Math.round(size * 0.4), ...style }
      }
      {...props}
    >
      {initials}
    </span>
  ),
)
FxAvatar.displayName = 'FxAvatar'
